/** Small colour helpers for deriving a brand palette from one hex value. */

export function normaliseHex(input: string | null | undefined): string | null {
  if (!input) return null;
  let hex = input.trim().replace(/^#/, "");
  if (hex.length === 3) hex = hex.replace(/(.)/g, "$1$1");
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `#${hex.toLowerCase()}`;
}

export function hexToRgb(hex: string): [number, number, number] {
  const clean = normaliseHex(hex) ?? "#000000";
  return [
    Number.parseInt(clean.slice(1, 3), 16),
    Number.parseInt(clean.slice(3, 5), 16),
    Number.parseInt(clean.slice(5, 7), 16),
  ];
}

function srgbToLinear(channel: number) {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Pick black or white ink for text sitting on `hex`. */
export function readableInk(hex: string, dark = "#06091a", light = "#ffffff"): string {
  return contrastRatio(hex, dark) >= contrastRatio(hex, light) ? dark : light;
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${r} ${g} ${b} / ${alpha})`;
}

function clamp(n: number, min = 0, max = 255) {
  return Math.min(max, Math.max(min, n));
}

export function lighten(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c: number) => clamp(Math.round(c + (255 - c) * amount));
  return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

export function darken(hex: string, amount: number): string {
  const [r, g, b] = hexToRgb(hex);
  const mix = (c: number) => clamp(Math.round(c * (1 - amount)));
  return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * A brand colour that reads well on a page has to sit inside a lightness band.
 * A logo-extracted colour often doesn't, so nudge it until it clears 3:1
 * against the surface it will be drawn on.
 */
export function fitToSurface(hex: string, surface: string, minContrast = 3.2): string {
  const surfaceIsDark = relativeLuminance(surface) < 0.2;
  let candidate = hex;
  for (let i = 0; i < 12 && contrastRatio(candidate, surface) < minContrast; i++) {
    candidate = surfaceIsDark ? lighten(candidate, 0.1) : darken(candidate, 0.1);
  }
  return candidate;
}
